import { Box, Collapse, Grid, Typography } from '@mui/material';
import { IconButton } from 'design-system';
import { ReactNode, useMemo, useState } from 'react';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import CustomCard from 'components/CustomCard';
import { useFeatures } from 'hooks/useFeatures';
import { isRemovedQuoteLine, showGroup } from 'utils/form';
import {
  CardFragment as Card,
  CardIntent,
  Field,
  FieldGroupFragment as FieldGroup,
  FieldGroupOrientation,
  FieldGroupVariant,
  FormType,
  InputType,
} from 'generated/graphql';
import FormField from './FormField';

export const TWO_COL_FORMS: FormType[] = [FormType.ProfileQuickActions];

interface FormGroupsProps {
  hasWhiteFields?: boolean;
  fieldGroups: FieldGroup[];
  isLoading?: boolean;
  isFormDialog: boolean;
  onFieldUpdateNew: (
    fieldGroup: Field[],
    field: Field,
    value: string,
    values?: string[],
    entityID?: string,
  ) => Promise<void>;
  onRefresh?: () => void;
  onFormRefresh?: () => void;
  autoFocusGroup?: number;
  isQCStorage?: boolean;
  formType: FormType;
  onClose?: () => void;
  node?: ReactNode;
  formFields?: Field[];
}

interface FormGroupProps extends FormGroupsProps {
  group: FieldGroup;
  groupIndex: number;
}

const isLogoutCard = (card: Card) => {
  return card.button.value === 'Logout';
};

const FormGroup = ({
  group,
  groupIndex,
  fieldGroups,
  hasWhiteFields = false,
  onFieldUpdateNew,
  isLoading = false,
  onRefresh,
  onFormRefresh,
  isFormDialog = false,
  autoFocusGroup = -1,
  isQCStorage = false,
  formType,
  onClose,
  node,
  formFields,
}: FormGroupProps) => {
  const { isEnabled } = useFeatures();
  const isTwoColForm = TWO_COL_FORMS.includes(formType);
  const isHorizontal = group.orientation === FieldGroupOrientation.Horizontal;
  // Sized fields are Grid items, which need a Grid container in place of the orientation's flex row.
  const isGrid = group.fields.some((field) => field.size);

  const firstField =
    autoFocusGroup === groupIndex ? group.fields.findIndex((field) => field.type !== InputType.Hidden) : -1;
  const [expanded, setExpanded] = useState(true);
  const allFields = useMemo(() => {
    const fields: Field[] = [];
    fieldGroups.forEach((fg) => {
      fields.push(...fg.fields);
    });
    return fields;
  }, [fieldGroups]);

  if (!showGroup(group)) return null;

  const isExpandVariant = group.variant === FieldGroupVariant.Expand;
  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  const fieldInputs = group.fields.map((field, fieldIndex, fieldGroup) => (
    <FormField
      key={field.id}
      hasWhiteFields={hasWhiteFields}
      field={field}
      autoFocus={fieldIndex === firstField}
      fieldGroup={fieldGroup}
      allFields={allFields}
      fieldIndex={fieldIndex}
      disabled={isLoading}
      onChange={onFieldUpdateNew}
      isHorizontal={isHorizontal}
      isQCStorage={isQCStorage}
      formType={formType}
      onRefresh={onRefresh}
      onFormRefresh={onFormRefresh}
    />
  ));

  const fieldsContent = (
    <Box
      sx={
        isHorizontal && !isGrid
          ? {
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              gap: 1,
            }
          : {}
      }
    >
      {isGrid ? (
        <Grid container columnSpacing={1}>
          {fieldInputs}
        </Grid>
      ) : (
        fieldInputs
      )}
      {group.cards && group.cards.length > 0 && (
        <>
          <Grid
            container
            sx={{
              justifyContent: 'flex-start',
              gap: '1.2%',
              ...(isTwoColForm && { rowGap: '12px' }),
            }}
          >
            {group.cards
              .filter((card) => !isLogoutCard(card))
              .map((card, index) => {
                const isFullWidthCard = isTwoColForm && card.context !== CardIntent.MenuItem;
                const md = isTwoColForm ? (isFullWidthCard ? 12 : 5.9) : isFormDialog ? 12 : 3.9;
                return (
                  <Grid key={`${card.title}-${index}`} size={{ md }} sx={{ width: '100%' }}>
                    <CustomCard
                      key={index}
                      data={card}
                      isLoading={isLoading}
                      onRefresh={onRefresh}
                      onFormRefresh={onFormRefresh}
                      onClose={onClose}
                      compact={isTwoColForm && card.context === CardIntent.MenuItem}
                    />
                  </Grid>
                );
              })}
          </Grid>
          {node}
          {group.cards
            .filter((card) => isLogoutCard(card))
            .map((card, index) => (
              <CustomCard
                key={index}
                data={card}
                isLoading={isLoading}
                onRefresh={onRefresh}
                onFormRefresh={onFormRefresh}
                onClose={onClose}
              />
            ))}
        </>
      )}
    </Box>
  );

  if (isExpandVariant) {
    return (
      <Box
        key={`${group.title}-${groupIndex}`}
        sx={(theme) => ({
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: 1,
          padding: theme.spacing(2),
          mb: theme.spacing(2),
        })}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            mb: expanded ? 1 : 0,
          }}
          onClick={handleToggleExpand}
        >
          {group.title ? (
            <Typography
              sx={(theme) => ({
                fontSize: '1rem',
                fontWeight: theme.typography.fontWeightMedium,
              })}
            >
              {group.title}
            </Typography>
          ) : (
            <Box />
          )}
          <IconButton
            track={false}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleExpand();
            }}
          >
            {expanded ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
          </IconButton>
        </Box>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {fieldsContent}
        </Collapse>
      </Box>
    );
  }

  return (
    <>
      <Box key={`${group.title}-${groupIndex}`}>
        {group.title && (
          <Typography
            sx={(theme) => ({
              fontSize: '1rem',
              mb: theme.spacing(0.5),
              mr: 1,
              fontWeight: theme.typography.fontWeightMedium,
            })}
          >
            {group.title}
          </Typography>
        )}
        {fieldsContent}
      </Box>
    </>
  );
};

const FormGroups = ({
  hasWhiteFields = false,
  fieldGroups,
  onFieldUpdateNew,
  isLoading = false,
  onRefresh,
  onFormRefresh,
  isFormDialog = false,
  autoFocusGroup = -1,
  isQCStorage = false,
  formType,
  onClose,
  node,
  formFields,
}: FormGroupsProps) => {
  const { isEnabled } = useFeatures();
  const isQuotesSearchEnabled = isEnabled('QuotesSearch');
  return (
    <>
      {fieldGroups?.map((group, groupIndex) =>
        isRemovedQuoteLine(group, formType, isQuotesSearchEnabled) ? null : (
          <FormGroup
            key={group.title + '-' + groupIndex}
            group={group}
            groupIndex={groupIndex}
            fieldGroups={fieldGroups}
            hasWhiteFields={hasWhiteFields}
            onFieldUpdateNew={onFieldUpdateNew}
            isLoading={isLoading}
            onRefresh={onRefresh}
            onFormRefresh={onFormRefresh}
            isFormDialog={isFormDialog}
            autoFocusGroup={autoFocusGroup}
            isQCStorage={isQCStorage}
            formType={formType}
            onClose={onClose}
            node={node}
            formFields={formFields}
          />
        ),
      )}
    </>
  );
};

export default FormGroups;
